import { useCallback, useEffect, useRef, useState } from 'react'

// MP-OS-190: the one download guard every download button uses, so they all behave the same.
// - One click makes one file. The guard is a ref checked synchronously, because `disabled` alone
//   cannot stop a second click that arrives before React re-renders. It stays closed while the
//   run is in flight AND while the short "done" label shows, so a fast triple click on an export
//   that finishes instantly (a CSV built in the browser) still downloads once.
// - `busy` / `done` drive a visible progress and success label on the button. `locked` (busy or
//   done) is when the guard ignores clicks, so buttons disable on it and their state matches.
// - A run that has nothing to do (no data yet, a missing node) returns SKIPPED: no success label,
//   no error, and the guard opens at once. Returning normally means a file was made.
// - A failure (or `timeoutMs` passing) sets `error`; `retry()` runs the last call again.
// - The run gets an AbortSignal that is aborted on timeout and on unmount, so a stale run never
//   saves a file after the UI has moved on (or a second one after a retry).

export interface DownloadOptions {
  timeoutMs?: number // give up (abort + error) after this long; none by default
  doneMs?: number // how long the success label shows, and further clicks are ignored
}

// MPD-76 F3: what a run returns when it returned early without making a file.
export const SKIPPED = 'skipped'
export type RunResult = void | typeof SKIPPED

export interface Download<A extends unknown[]> {
  start: (...args: A) => void
  retry: () => void
  busy: boolean
  done: boolean
  locked: boolean // busy or done: clicks are ignored, so the button is disabled
  error: boolean
  pending: A | null // the arguments of the running or just-finished call (e.g. 'png' or 'pdf')
}

export const DOWNLOAD_DONE_MS = 1600

export function useDownload<A extends unknown[] = []>(
  run: (signal: AbortSignal, ...args: A) => Promise<RunResult> | RunResult,
  { timeoutMs, doneMs = DOWNLOAD_DONE_MS }: DownloadOptions = {},
): Download<A> {
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState(false)
  const [pending, setPending] = useState<A | null>(null)
  const runRef = useRef(run)
  runRef.current = run
  const inFlightRef = useRef(false)
  const tokenRef = useRef(0) // invalidates a stale timeout/result after a retry
  const controllerRef = useRef<AbortController | null>(null)
  const lastArgsRef = useRef<A | null>(null)
  const doneTimerRef = useRef<number | undefined>(undefined)
  const mountedRef = useRef(true)

  // StrictMode mounts, unmounts and remounts once in dev: reset on (re)mount, not only on unmount.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      tokenRef.current += 1
      controllerRef.current?.abort()
      window.clearTimeout(doneTimerRef.current)
      inFlightRef.current = false
    }
  }, [])

  const start = useCallback((...args: A) => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    const token = ++tokenRef.current
    const controller = new AbortController()
    controllerRef.current = controller
    lastArgsRef.current = args
    window.clearTimeout(doneTimerRef.current)
    setBusy(true)
    setDone(false)
    setError(false)
    setPending(args)
    const current = () => mountedRef.current && tokenRef.current === token
    let timer: number | undefined
    if (timeoutMs != null) {
      timer = window.setTimeout(() => {
        controller.abort()
        if (!current()) return
        inFlightRef.current = false // a hung run must not block a retry
        setBusy(false)
        setError(true)
      }, timeoutMs)
    }
    void (async () => {
      try {
        const result = await runRef.current(controller.signal, ...args)
        if (controller.signal.aborted || !current()) return
        setBusy(false)
        if (result === SKIPPED) {
          setPending(null)
          inFlightRef.current = false
          return
        }
        setDone(true)
        doneTimerRef.current = window.setTimeout(() => {
          if (!current()) return
          setDone(false)
          inFlightRef.current = false
        }, doneMs)
      } catch {
        if (controller.signal.aborted || !current()) return
        setBusy(false)
        setError(true)
        inFlightRef.current = false
      } finally {
        window.clearTimeout(timer)
      }
    })()
  }, [timeoutMs, doneMs])

  const retry = useCallback(() => {
    if (lastArgsRef.current) start(...lastArgsRef.current)
  }, [start])

  return { start, retry, busy, done, locked: busy || done, error, pending }
}
